<template>
  <el-dialog
    v-model="visible"
    :title="dialogTitle"
    :width="800"
    @close="handleClose"
    destroy-on-close
  >

    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="110px"
      :disabled="isView"
      style="padding: 20px"
    >
      <!-- 分类名称 -->
      <el-form-item label="分类名称" prop="catName">
        <el-input v-model="form.catName" placeholder="请输入分类名称" style="width: 280px" />
      </el-form-item>

      <!-- 分类下商品：默认展示 + 远程搜索 -->
      <el-form-item label="分类下商品" prop="bindCatSpuList">
        <el-select
          v-model="form.bindCatSpuList"
          multiple
          filterable
          remote
          reserve-keyword
          placeholder="请选择商品 (可输入名称搜索)"
          :remote-method="remoteSearchGoods"
          :loading="searchLoading"
          @focus="handleFocus"
          style="width: 100%"
        >
          <!-- 对应低代码 tpl: '${spuName}' -->
          <el-option
            v-for="item in spuOptions"
            :key="item.spuCode"
            :label="item.spuName"
            :value="item.spuCode"
          >
            <div class="option-item">
              <span>{{ item.spuName }}</span>
              <span class="option-code">{{ item.spuCode }}</span>
            </div>
          </el-option>
        </el-select>
        <div >
          会根据所选商品顺序进行初始排序
        </div>
      </el-form-item>
      <div style="height: 20px;"/>

      <!-- 上架后状态 -->
      <el-form-item label="上架后状态" prop="catStatus">
        <el-radio-group v-model="form.catStatus">
          <el-radio label="grounding">立即显示</el-radio>
          <el-radio label="off_shelf">隐藏</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>

<!--    <CFormItemOssUpload />-->

    <template #footer v-if="!isView">
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">
        确定
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from 'vue'
import {onMounted, useCurrentAppInstance} from '@zto/zpage'
const app = useCurrentAppInstance()
import { ElMessage, type FormInstance } from 'element-plus'
import CFormItemOssUpload from "@/components/common/CFormItemOssUpload.vue";

type DialogType = 'ADD' | 'EDIT' | 'VIEW'

const props = defineProps<{
  apiSubmit?: (data: any) => Promise<any>
}>()
const emit = defineEmits(['refresh'])

// --- 状态 ---
const visible = ref(false)
const mode = ref<DialogType>('ADD')
const submitting = ref(false)
const searchLoading = ref(false)
const formRef = ref<FormInstance>()

const spuOptions = ref<any[]>([]) // 下拉列表数据源源源源

const form = reactive({
  id: '',
  catName: '',
  bindCatSpuList: [] as string[],
  catStatus: 'grounding'
})

const rules = {
  catName: [{ required: true, message: '请输入分类名称', trigger: 'blur' }],
  bindCatSpuList: [{ required: false,message: '请选择商品', trigger: 'change' }],
  catStatus: [{ required: true, message: '请选择状态', trigger: 'change' }]
}

const isView = computed(() => mode.value === 'VIEW')
const dialogTitle = computed(() => ({ ADD: '新增分类', EDIT: '编辑分类', VIEW: '查看分类' }[mode.value]))

// --- 逻辑核心 ---

/**
 * 获取商品数据 (对应低代码 api: 'pc/catInfo/queryGroundingSpuInfoWithoutCat')
 */
const fetchSpuData = async (keyword = '') => {
  searchLoading.value = true
  try {
    // 模拟 API 请求参数 (对应低代码 apiParams)
    const params = {
      pageSize: 100,
      pageIndex: 1,
      keyword: keyword // 搜索关键字
    }
    console.log('请求商品接口, 参数:', params)

    // 这里应该是你的真实请求: const res = await axios.get('...', { params })
    // 下面用你提供的 JSON 数据模拟
    // const mockResult = [
    //   { spuCode: "201d3a08-bc19-4442-b770-43bc3278b6e8", spuName: "测试商品-08" },
    //   { spuCode: "d76b96af-9a18-4500-860e-25e6851a9e37", spuName: "测试商品-AF" },
    //   { spuCode: "b13f6b6f-d2d6-476c-9ac6-484adb5f1335", spuName: "测试商品-6F" },
    //   { spuCode: "131231", spuName: "测试商品-131" }
    // ]
    const res = await app.request({
      api: 'pc/goods/queryGroundingSpuInfoWithoutCat',
      params
    })
    // 过滤逻辑：如果是远程搜索，通常后端处理。如果是本地模拟，如下：
    const filtered = keyword
      ? res?.filter(i => i.spuName.includes(keyword) || i.spuCode.includes(keyword))
      : res

    // 重点：如果是编辑模式，需要保留已选中的项，否则下拉框无法显示已选的 Label
    const selectedItems = spuOptions.value.filter(item => form.bindCatSpuList.includes(item.spuCode))

    // 合并【当前选中的】和【新查出来的】，并去重
    const combined = [...selectedItems, ...filtered]
    const map = new Map()
    spuOptions.value = combined.filter(item => !map.has(item.spuCode) && map.set(item.spuCode, 1))

  } catch (error) {
    console.error(error)
  } finally {
    searchLoading.value = false
  }
}

/**
 * 暴露的打开方法 (onShow)
 */
const open = async (type: DialogType, data?: any) => {
  mode.value = type
  visible.value = true

  // 1. 初始化表单
  Object.assign(form, {
    id: data?.id || '',
    catName: data?.catName || '',
    catStatus: data?.catStatus || 'grounding',
    bindCatSpuList: []
  })

  // 2. 对应 onShow: 处理编辑时的初始商品
  if (data?.spuInfoVOList) {
    spuOptions.value = [...data.spuInfoVOList]
    form.bindCatSpuList = data.spuInfoVOList.map((s: any) => s.spuCode)
  } else {
    spuOptions.value = []
  }

  // 3. 核心：默认加载前100条商品展示 (实现默认展示)
  await fetchSpuData('')
}

/**
 * 远程搜索 (fuzzy-select 逻辑)
 */
const remoteSearchGoods = (query: string) => {
  fetchSpuData(query)
}

/**
 * 获取焦点时如果没数据也请求一次
 */
const handleFocus = () => {
  if (spuOptions.value.length === 0) {
    fetchSpuData('')
  }
}

/**
 * 提交 (beforeSubmit)
 */
const handleSubmit = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (valid) {
      submitting.value = true
      try {
        const payload = {
          id: form.id,
          catName: form.catName,
          catStatus: form.catStatus,
          // 格式化提交数据：spuCode -> {spuCode, sort: 1}
          bindCatSpuList: form.bindCatSpuList.map(code => ({
            spuCode: code,
            sort: 1
          }))
        }
        if (props.apiSubmit) await props.apiSubmit(payload)
        ElMessage.success('保存成功')
        visible.value = false
        emit('refresh')
      } finally {
        submitting.value = false
      }
    }
  })
}

const handleClose = () => {
  formRef.value?.resetFields()
}


defineExpose({ open })
</script>

<style scoped>
.option-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.option-code {
  color: #8492a6;
  font-size: 12px;
  margin-left: 10px;
}
</style>
